/**
 * Service page block type system.
 *
 * Each service page's content JSONB can optionally include a `_blocks` array
 * that controls which sections are visible and in what order.
 * If `_blocks` is absent, the default order is used (backward compatible).
 *
 * Block data is NOT duplicated — it lives in the existing flat content fields
 * (headline, problemTitle, services, etc.). Blocks only control ordering and visibility.
 */

export interface ServiceBlock {
  type: string;
  enabled: boolean;
}

export interface BlockTypeDefinition {
  label: string;
  description: string;
  /** Fields in the content object that this block uses */
  fields: string[];
}

export const BLOCK_TYPES: Record<string, BlockTypeDefinition> = {
  hero: {
    label: "Hero",
    description: "Main headline, subheadline, and CTA button",
    fields: ["categoryLabel", "headline", "subheadline", "heroSecondaryText", "ctaText", "ctaLink"],
  },
  problem: {
    label: "Problem Framing",
    description: "Diagnostic bullet points highlighting pain points",
    fields: ["problemTitle", "problems"],
  },
  solution: {
    label: "Solution Overview",
    description: "Outcome-focused solution points with checkmarks",
    fields: ["solutionTitle", "solutionPoints"],
  },
  platform_ecosystems: {
    label: "Platform Ecosystems",
    description: "Platform-native advertising context section",
    fields: ["platformEcosystems"],
  },
  services_grid: {
    label: "Services Grid",
    description: "Card grid showing service components and outcomes",
    fields: ["systemTitle", "systemDescription", "services"],
  },
  system_shift: {
    label: "System Shift (Before/After)",
    description: "Before vs After comparison table",
    fields: ["systemShift"],
  },
  process: {
    label: "Process / Timeline",
    description: "Step-by-step process or interactive timeline",
    fields: ["processTitle", "processSteps", "timelinePhases"],
  },
  data_flowchart: {
    label: "Data Flowchart",
    description: "Visual input-to-output data flow diagram",
    fields: ["dataFlowchart"],
  },
  capability_matrix: {
    label: "Capability Matrix",
    description: "Categorized capabilities with hover-reveal depth",
    fields: ["capabilityMatrix"],
  },
  engagement_shape: {
    label: "Engagement Shape",
    description: "Structural four-phase engagement model",
    fields: ["engagementShape"],
  },
  tech_stack: {
    label: "Technology Stack",
    description: "Tools and platforms used",
    fields: ["technologyCategories", "technologies", "techTitle", "techDescription"],
  },
  qualification: {
    label: "Qualification (For/Not For)",
    description: "Who this service is and isn't for",
    fields: ["qualification"],
  },
  faqs: {
    label: "FAQs",
    description: "Frequently asked questions",
    fields: ["faqs"],
  },
  final_cta: {
    label: "Final CTA",
    description: "Closing call to action",
    fields: ["finalCtaTitle", "finalCtaDescription", "finalCtaButtonText"],
  },
};

/** Default section order matching the original fixed layout */
export const DEFAULT_BLOCK_ORDER: ServiceBlock[] = [
  { type: "hero", enabled: true },
  { type: "problem", enabled: true },
  { type: "solution", enabled: true },
  { type: "platform_ecosystems", enabled: true },
  { type: "services_grid", enabled: true },
  { type: "system_shift", enabled: true },
  { type: "process", enabled: true },
  { type: "data_flowchart", enabled: true },
  { type: "capability_matrix", enabled: true },
  { type: "engagement_shape", enabled: true },
  { type: "tech_stack", enabled: true },
  { type: "qualification", enabled: true },
  { type: "faqs", enabled: true },
  { type: "final_cta", enabled: true },
];

/** Get the block label for a given type */
export const getBlockLabel = (type: string): string =>
  BLOCK_TYPES[type]?.label || type;

/** Get the block description for a given type */
export const getBlockDescription = (type: string): string =>
  BLOCK_TYPES[type]?.description || "";

/** Get all available block types not yet in the list */
export const getAvailableBlockTypes = (currentBlocks: ServiceBlock[]): string[] => {
  const usedTypes = new Set(currentBlocks.map((b) => b.type));
  return Object.keys(BLOCK_TYPES).filter((t) => !usedTypes.has(t));
};
